import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const principles = [{
  number: "01",
  leftLabel: "NEUTRAL",
  rightLabel: "STRUCTURE",
  title: "NEUTRAL OVERSIGHT",
  description: "In accordance with operational policy, we maintain a strictly neutral environment. Our monitors facilitate parenting time without bias, ensuring that all interactions remain objective and free from the influence of external litigation or parental conflict.",
  attribution: "— Agency Policy: Neutrality & Oversight Standards"
}, {
  number: "02",
  leftLabel: "TRAUMA",
  rightLabel: "INFORMED",
  title: "TRAUMA-INFORMED",
  description: "Our practice adheres to trauma-informed care protocols, recognizing the complex emotional needs of families in transition. We implement specific safety and engagement strategies designed to mitigate secondary trauma and support the child's psychological safety.",
  attribution: "— Clinical Policy: Trauma-Informed Engagement"
}, {
  number: "03",
  leftLabel: "COURT",
  rightLabel: "REPORTING",
  title: "DETAILED REPORTING",
  description: "Per governing reporting policy, we provide the court with comprehensive, factual documentation of all visits. Our reports focus on observable behaviors and interactions, ensuring a transparent record that assists judicial officers in determining the best interests of the child.",
  attribution: "— Governing Policy: Documentation & Reporting Compliance"
}, {
  number: "04",
  leftLabel: "CHILD",
  rightLabel: "CENTERED",
  title: "CHILD-FOCUSED",
  description: "Our child-focused service model prioritizes the physical and emotional security of the minor at all times. All supervision activities are conducted through the lens of the child's experience, ensuring their rights and well-being remain the primary focus of the intervention.",
  attribution: "— Policy Document: Child-Centered Service Delivery"
}];
export default function Expectations() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: closingRef,
    isVisible: closingVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4930" id="what-to-expect" className="relative overflow-hidden bg-background text-foreground">
      <div aria-hidden="true" className="absolute inset-0 opacity-[0.04] pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/desert_texture.png)] bg-repeat bg-center" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-44 lg:py-[180px]">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-24 md:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <div className="font-default text-xs md:text-sm font-bold tracking-[0.3em] mb-6 md:mb-8 text-foreground">
              OUR SERVICE STANDARDS
            </div>
            <h2 className="font-default font-bold leading-[0.9] text-foreground" style={{
            fontSize: "clamp(3rem, 8vw, 7rem)",
            letterSpacing: "-0.05em"
          }}>
              CORE /<br />PRINCIPLES.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:items-end">
            <p className="font-default leading-relaxed max-w-[480px] text-foreground/80" style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)"
          }}>
              These pillars define our commitment to safety, neutrality, and professional integrity in every family we support.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 relative">
          {principles.map((principle, i) => {
          return <PrincipleItem key={principle.number} data-index={i} principle={principle} index={i} total={principles.length} />;
        })}
        </div>

        <div ref={closingRef} className={`mt-32 md:mt-44 lg:mt-52 text-center transition-all duration-1000 ${closingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h3 className="font-default font-bold leading-[0.95] text-foreground" style={{
          fontSize: "clamp(2.25rem, 6vw, 5.5rem)",
          letterSpacing: "-0.04em"
        }}>
            STEADY BOUNDARIES.<br />
            COMPASSIONATE CARE.{" "}
            <span className="text-primary">ALWAYS.</span>
          </h3>
        </div>
      </div>
    </section>;
}
function PrincipleItem({
  principle,
  index,
  total,
  ...rest
}: {
  principle: {
    number: string;
    leftLabel: string;
    rightLabel: string;
    title: string;
    description: string;
    attribution: string;
  };
  index: number;
  total: number;
} & React.HTMLAttributes<HTMLDivElement>) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div {...rest} ref={ref} className={`relative px-0 md:px-6 lg:px-8 py-12 md:py-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 120}ms`,
    borderTop: "1px solid var(--secondary)",
    borderRight: index < total - 1 ? "1px solid var(--muted)" : "none"
  }}>
      <div className="relative mb-8 md:mb-10 leading-none">
        <span aria-hidden="true" className="absolute font-default font-bold leading-none select-none text-foreground/10" style={{
        fontSize: "clamp(4rem, 7vw, 8rem)",
        letterSpacing: "-0.05em",
        top: "8px",
        left: "8px"
      }}>
          {principle.number}
        </span>
        <span className="relative font-default font-bold leading-none block text-primary" style={{
        fontSize: "clamp(4rem, 7vw, 8rem)",
        letterSpacing: "-0.05em",
        WebkitTextStroke: "1px var(--secondary)"
      }}>
          {principle.number}
        </span>
      </div>

      <div className="relative mb-6 md:mb-8 pt-6">
        <div className="absolute top-0 left-0 right-0 border-t border-secondary" />
        <div className="flex justify-between items-center font-default text-[10px] md:text-xs font-bold tracking-[0.2em] text-foreground">
          <span>{principle.leftLabel}</span>
          <span>{principle.rightLabel}</span>
        </div>
      </div>

      <h3 className="font-default font-bold mb-6 md:mb-8 leading-[0.95] text-foreground" style={{
      fontSize: "clamp(1.75rem, 2.4vw, 3rem)",
      letterSpacing: "-0.04em"
    }}>
        {principle.title}
      </h3>

      <p className="font-default leading-relaxed mb-8 text-foreground/90" style={{
      fontSize: "16px"
    }}>
        {principle.description}
      </p>

      <p className="font-default italic text-sm text-foreground/70">
        {principle.attribution}
      </p>
    </div>;
}